import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Papa from 'papaparse';

import { axios } from '@/services/axios';
import { downloadCSV, extractUrlParams } from '@/utils/utils';
import { API_RESULT_FIELDS_TO_EXPORT } from './useCSVCallingRates';

const DEFAULT_BULK_UPDATE_STATUS = {
    apiErrors: '',
};
const ERROR_MESSAGE = 'Something went wrong. Please try again later.';
const FILENAME = 'FailedUploadingData.csv';

/** Plan Rates */
export default function useBulkUpdate() {
    const [error, setError] = useState([]);
    const [loading, setLoading] = useState(false);
    const [succeed, setSucceed] = useState([]);
    const [counter, setCounter] = useState(0);
    const [errorCounter, setErrorCounter] = useState(0);
    const [uploadPayloads, setUploadPayloads] = useState([]);
    const isIgnoredFilter = (v) => v.ignored;
    const { requiredScope } = useSelector((state) => state.login);
    const { pathname } = useSelector((state) => state.url);
    const { planID } = extractUrlParams(pathname);

    //reset UI
    const handleReset = () => {
        setLoading(false);
        setCounter(0);
        setErrorCounter(0);
        setSucceed([]);
        setError([]);
        setUploadPayloads([]);
    };

    //after API calls, set UI State
    const handleFinishBulkUpdate = ({ errors, responses }) => {
        setError(errors);
        setSucceed(responses);
    };

    //handle api calls
    const handleBulkUpdate = async (payloads) => {
        const errors = [];
        const responses = [];
        setUploadPayloads(payloads);
        payloads.forEach(async (payload, index) => {
            try {
                //api call for calling rates
                const response = await (payload.isNewRate
                    ? axios.post('/rate/', payload, {
                          headers: {
                              'X-RequestScope': requiredScope,
                          },
                      })
                    : axios.put('/rate/', payload, {
                          headers: {
                              'X-RequestScope': requiredScope,
                          },
                      }));
                //then store the success response
                responses.push(response);
            } catch (e) {
                //put to failed table
                console.error(e?.response);
                errors.push({
                    ...payload,
                    apiErrors: `${e?.response?.status === 400 ? e?.response?.data?.message : ERROR_MESSAGE}`,
                });
                //count as error
                setErrorCounter((prev) => prev + 1);
            } finally {
                //set UI counter
                setCounter((prev) => prev + 1);
                if (index === payloads.length - 1) {
                    //setState for Failed & Succeed table
                    handleFinishBulkUpdate({ errors, responses });
                }
            }
        });
    };

    //add more UI states to BE payloads.
    const transformSubmitData = (payloads) => {
        return payloads
            .filter((v) => !isIgnoredFilter(v))
            .map((payload) => ({
                ...DEFAULT_BULK_UPDATE_STATUS,
                ...(payload.isNewRate
                    ? {
                          name: payload.name,
                          priority: payload.priority,
                          prefix: payload.prefix,
                          flagFallOutRate: payload.flagFallOutRate ?? 0,
                          flagFallInRate: payload.flagFallInRate ?? 0,
                          inboundRate: payload.inboundRate ?? 0,
                          outboundRate: payload.outboundRate ?? 0,
                          isActive: true,
                          isNewRate: true,
                          planID,
                      }
                    : {
                          name: payload.name,
                          priority: payload.priority,
                          prefix: payload.prefix,
                          flagFallOutRate: payload.flagFallOutRate ?? 0,
                          flagFallInRate: payload.flagFallInRate ?? 0,
                          inboundRate: payload.inboundRate ?? 0,
                          outboundRate: payload.outboundRate ?? 0,
                          isActive: payload.isActive,
                          planID,
                          id: payload.id,
                          ...payload,
                      }),
            }));
    };

    //entry point to start API call
    const processSubmission = async (payloads) => {
        setLoading(true);
        const finalPayloads = transformSubmitData(payloads);
        handleBulkUpdate(finalPayloads);
    };

    /**
     * handle UI button - start api call when payload is ready.
     */
    const handlePayloadChange = useCallback((payloads) => {
        processSubmission(payloads);
    }, []);

    /**
     * transform BE JSON field to more human readable CSV heading columns.
     * @param {*} data
     * @returns
     */
    const transformJSONToCSV = (data) => {
        return data.map((rate) => {
            return Object.keys(API_RESULT_FIELDS_TO_EXPORT).reduce(
                (acc, key) => {
                    const value = rate[key];
                    const newKey = API_RESULT_FIELDS_TO_EXPORT[key];
                    acc[newKey] = value;
                    if (key === 'isActive') {
                        acc[newKey] = Boolean(value);
                    }
                    return acc;
                },
                {},
            );
        });
    };

    /**
     * handle UI button - export API calls that failed to CSV.
     */
    const handleExportFailedData = () => {
        const failedData = transformJSONToCSV(error);
        const res = Papa.unparse(failedData);
        downloadCSV(res, FILENAME);
    };

    return {
        handlePayloadChange,
        loading,
        counter,
        errorCounter,
        error,
        succeed,
        handleReset,
        handleExportFailedData,
        total: uploadPayloads.length,
    };
}
